import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { AlarmClock, AlertTriangle, Check } from "lucide-react";

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const end = new Date(isoDate);
  const now = new Date();
  const ms = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function TrialExpiredPaywall({
  deletionDate,
  reason = "trial",
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exportNote, setExportNote] = useState(false);

  const email =
    user?.email ||
    (() => {
      try {
        const raw = localStorage.getItem("user");
        return raw ? JSON.parse(raw).email : "";
      } catch {
        return "";
      }
    })();

  const daysLeft = useMemo(() => daysUntil(deletionDate), [deletionDate]);

  const bullet = (text) => (
    <li key={text} className="flex gap-2 text-gray-600 text-sm">
      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-[480px] rounded-2xl border border-amber-500/30 bg-white p-8 shadow-2xl text-center space-y-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-expired-title"
      >
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlarmClock className="w-10 h-10 text-amber-400" />
          </div>
        </div>

        <h1
          id="trial-expired-title"
          className="text-xl font-bold text-ad-text"
        >
          {reason === "credits"
            ? "You've run out of credits"
            : "Your free trial has ended"}
        </h1>

        <p className="text-ad-label text-sm leading-relaxed">
          {reason === "credits"
            ? "Add more credits to continue using Agent David's AI features."
            : "Your 7-day trial of Agent David has expired."}
        </p>

        {reason === "trial" && deletionDate && daysLeft != null ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-left">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-amber-200/90 text-sm">
              Your data will be permanently deleted in{" "}
              <strong>{daysLeft}</strong> day{daysLeft !== 1 ? "s" : ""}{" "}
              unless you upgrade.
            </p>
          </div>
        ) : null}

        {reason === "trial" ? (
          <div className="text-left space-y-2 pt-2">
            <p className="text-gray-600 text-sm font-medium">
              Upgrade now to keep access to:
            </p>
            <ul className="space-y-1.5">
              {bullet("All your profiles and their data")}
              {bullet("Brain intelligence and insights")}
              {bullet("Chat Assistant and AI features")}
              {bullet("Target & Pacing and reporting")}
            </ul>
          </div>
        ) : null}

        <Button
          type="button"
          className="w-full glow-button"
          onClick={() => navigate("/Billing")}
        >
          {reason === "credits" ? "Add credits →" : "Choose a plan →"}
        </Button>

        <div className="pt-1">
          <button
            type="button"
            className="text-xs text-ad-muted hover:text-gray-600 underline underline-offset-2"
            onClick={() => setExportNote(true)}
          >
            Export my data
          </button>
          {exportNote ? (
            <p className="text-xs text-ad-label mt-2">
              We&apos;ll email your data to{" "}
              {email ? (
                <span className="text-ad-text">{email}</span>
              ) : (
                "your account email"
              )}{" "}
              within 24 hours.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
